import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { createPortal } from "react-dom";

import "./styles.css";

export interface FeedbackValue {
  rating: number;
  comment: string;
}

interface FeedbackOverlayProps {
  open: boolean;
  targetTitle: string;
  subjectLabel?: string;
  initialRating: number;
  initialComment?: string;
  hasFeedback?: boolean;
  onClose: () => void;
  onSubmit: (feedback: FeedbackValue) => void | Promise<void>;
}

const focusableSelector = [
  "button:not(:disabled)",
  "textarea:not(:disabled)",
  "[href]",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const FeedbackOverlay = ({
  open,
  targetTitle,
  subjectLabel = "do treinamento",
  initialRating,
  initialComment = "",
  hasFeedback = false,
  onClose,
  onSubmit,
}: FeedbackOverlayProps) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const [rating, setRating] = React.useState(initialRating);
  const [hoveredRating, setHoveredRating] = React.useState(0);
  const [comment, setComment] = React.useState(initialComment);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;

    setRating(initialRating);
    setHoveredRating(0);
    setComment(initialComment);
    setIsSubmitting(false);
  }, [initialComment, initialRating, open]);

  React.useEffect(() => {
    if (!open) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      );

      if (!focusableElements.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="feedback-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="feedback-overlay__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-overlay-title"
        aria-describedby="feedback-overlay-description"
        tabIndex={-1}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="feedback-overlay__close"
          onClick={onClose}
          aria-label="Fechar feedback"
        >
          <CloseRoundedIcon aria-hidden="true" />
        </button>

        <form
          className="feedback-overlay__form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!rating || isSubmitting) return;

            try {
              setIsSubmitting(true);
              await onSubmit({ rating, comment: comment.trim() });
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          <h2 id="feedback-overlay-title">FEEDBACK</h2>
          <p id="feedback-overlay-description">O que achou {subjectLabel}?</p>
          <strong className="feedback-overlay__target">{targetTitle}</strong>

          <div
            className="feedback-overlay__stars"
            onMouseLeave={() => setHoveredRating(0)}
            aria-label="Nota do feedback"
          >
            {Array.from({ length: 5 }, (_, index) => {
              const value = index + 1;
              const isHighlighted = value <= (hoveredRating || rating);

              return (
                <button
                  type="button"
                  key={value}
                  className={isHighlighted ? "is-selected" : ""}
                  onMouseEnter={() => setHoveredRating(value)}
                  onFocus={() => setHoveredRating(value)}
                  onBlur={() => setHoveredRating(0)}
                  onClick={() => setRating(value)}
                  aria-label={`${value} ${value === 1 ? "estrela" : "estrelas"}`}
                  aria-pressed={rating === value}
                >
                  <StarRoundedIcon aria-hidden="true" />
                </button>
              );
            })}
          </div>

          <label className="sr-only" htmlFor="feedback-overlay-comment">
            Comentário
          </label>
          <textarea
            id="feedback-overlay-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Comentário..."
            maxLength={1000}
          />

          <button
            type="submit"
            className="feedback-overlay__submit"
            disabled={!rating || isSubmitting}
          >
            {isSubmitting
              ? "Enviando..."
              : hasFeedback
              ? "Alterar Feedback"
              : "Adicionar Feedback"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackOverlay;
